import React, { useEffect, useState, useRef } from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import useAuth from '../hooks/useAuth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPen, faFloppyDisk, faTrash, faPlus, faLink, faLinkSlash } from '@fortawesome/free-solid-svg-icons';
import '../css/guild-profile.css';

// Set up modal root
Modal.setAppElement('#app');

const GuildProfile = () => {
  const { user, displayName, discordServers } = useAuth();
  const [guildData, setGuildData] = useState(null);
  const [guildAdminData, setGuildAdminData] = useState(null);
  const [guildManagerData, setGuildManagerData] = useState(null);
  const [guildChannelData, setGuildChannelData] = useState([]);
  const [guildGameData, setGuildGameData] = useState([]);
  const [editingGames, setEditingGames] = useState({});
  const [loading, setLoading] = useState(true);

  // Update document title based on game name and guild name
  const originalTitle = useRef(document.title);
  useEffect(() => {
    if (guildData && guildData.name) {
      document.title = `${originalTitle.current} x ${guildData.name}`;
    } else {
      document.title = originalTitle.current;
    }
  }, [guildData]);

  // New channel modal state
  const [showNewChannelModal, setShowNewChannelModal] = useState(false);
  const [newChannelForm, setNewChannelForm] = useState({
    platform: "kick",
    channel: ""
  });

  // New game modal state and form state
  const [showNewGameModal, setShowNewGameModal] = useState(false);
  const [newGameForm, setNewGameForm] = useState({
    game_name: "",
    game_type: "raffle-basic",
    keyword_countdown_time: "60",
    keyword_style: "=",
    keyword: "",
    keyword_enter: "false"
  });

  // Environment variables
  const BASE_URL =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? process.env.REACT_APP_PRODUCTION_BASE_URL
      : process.env.REACT_APP_DEVELOPMENT_BASE_URL;
  const API_BASE_URL =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? process.env.REACT_APP_PRODUCTION_API_BASE_URL
      : process.env.REACT_APP_DEVELOPMENT_API_BASE_URL;
  const PORT =
    process.env.REACT_APP_ENVIRONMENT === 'production'
      ? process.env.REACT_APP_PRODUCTION_PORT
      : process.env.REACT_APP_DEVELOPMENT_PORT;

  let isAuthorized = false;
  let activeServer = {};
  const guildIdFromPath = window.location.pathname.split('/')[2];
  for (const server of discordServers) {
    if (server.id === guildIdFromPath) {
      isAuthorized = true;
      activeServer = server;
      break;
    }
  }
  if (!isAuthorized) {
    window.location.href = '/';
    return null;
  }

  // Initial fetch of guild data, admins, managers, channels and games
  useEffect(() => {
    if (activeServer.id) {
      axios.get(`${API_BASE_URL}:${PORT}/api/guild/${activeServer.id}`)
        .then(({ data }) => {
          console.debug('Guild data:', data);
          setGuildData(data);
        })
        .catch(err => console.error('Error fetching guild data:', err))
        .finally(() => setLoading(false));

      axios.get(`${API_BASE_URL}:${PORT}/api/guild/${activeServer.id}/admins`)
        .then(({ data }) => {
          console.debug('Guild admins:', data);
          setGuildAdminData(data);
        })
        .catch(err => console.error('Error fetching guild admins:', err));

      axios.get(`${API_BASE_URL}:${PORT}/api/guild/${activeServer.id}/managers`)
        .then(({ data }) => {
          console.debug('Guild managers:', data);
          setGuildManagerData(data);
        })
        .catch(err => console.error('Error fetching guild managers:', err));

      axios.get(`${API_BASE_URL}/api/games/${activeServer.id}/games`)
        .then(({ data }) => {
          console.debug('Guild games:', data);
          setGuildGameData(Object.values(data));
        })
        .catch(err => console.error('Error fetching guild games:', err));

      axios.get(`${API_BASE_URL}:${PORT}/api/channels/${activeServer.id}`)
        .then(({ data }) => {
          console.debug('Fetched channels:', data);
          const channelsArray = data && typeof data === 'object'
            ? Object.values(data)
            : [];
          setGuildChannelData(channelsArray);
        })
        .catch(err => console.error('Error fetching guild channels:', err));
    } else {
      setLoading(false);
    }
  }, [activeServer.id, API_BASE_URL, PORT]);

  // Polling effect: refresh games every 5 seconds.
  useEffect(() => {
    if (!activeServer.id) return;
    const interval = setInterval(() => {
      axios.get(`${API_BASE_URL}/api/games/${activeServer.id}/games`)
        .then(({ data }) => {
          // Preserve games that are being edited (by checking editingGames)
          setGuildGameData(prev =>
            Object.values(data).map(game => {
              if (editingGames.hasOwnProperty(game.id)) {
                const existing = prev.find(g => g.id === game.id);
                return existing ? existing : game;
              }
              return game;
            })
          );
        })
        .catch(err => console.error('Error refreshing guild games:', err));
    }, 5000);
    return () => clearInterval(interval);
  }, [activeServer.id, API_BASE_URL, PORT, editingGames]);

  if (loading) return <div>Loading...</div>;
  if (!guildData)
    return <div>Error loading guild data. Please try again later.</div>;

  // --- ChannelRow Component ---
  // This component uses the parent guildChannelData state.
  const ChannelRow = React.memo(({
    platform,
    channel,
    onToggleActive,
    onDelete
  }) => {
    let platformIcon;
    switch (platform) {
      case "kick":
      default:
        platformIcon = <img src="/img/kick/logo.svg" alt="Kick.com" />;
        break;
    }
    return (
      <div className="channel-row">
        <div className="channel-platform">
          {platformIcon}
        </div>
        <div className="channel-name">
          {channel.verified === "false" ? (
            <span className="unverified-channel">{channel.id}</span>
          ) : (
            <a
              href={`${BASE_URL}:${PORT}/c/${channel.platform}/${channel.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {channel.id}
            </a>
          )}
        </div>
        <div className="channel-actions">
          {channel.verified === "false" && channel.platform === "kick" ? (
            <a
              className="icon-button error"
              href={`${API_BASE_URL}:${PORT}/api/kick/auth?guild_id=${activeServer.id}&channel_id=${channel.id}`}
              rel="noopener noreferrer"
              title="Verify Channel"
            >
              <FontAwesomeIcon icon={faLinkSlash} />
            </a>
          ) : (
            <>
              <FontAwesomeIcon icon={faLink} />
              <label className="active-toggle">
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={channel.active === "true"}
                    onChange={onToggleActive}
                  />
                  <span className="slider" title="Enable/Disable Channel"></span>
                </div>
              </label>
            </>
          )}
          <a
            className="icon-button"
            onClick={onDelete}
            title="Delete Channel"
          >
            <FontAwesomeIcon icon={faTrash} />
          </a>
        </div>
      </div>
    );
  }, (prev, next) => {
    return prev.channel.id === next.channel.id &&
      prev.channel.platform === next.channel.platform &&
      prev.channel.active === next.channel.active;
  });

  // --- GameRow Component ---
  // This component uses the parent editingGames state.
  const GameRow = React.memo(({
    game,
    isEditing,
    tempName,
    onTempNameChange,
    onSave,
    onToggleActive,
    onDelete
  }) => {
    return (
      <div className="game-row">
        <div className="game-name">
          {isEditing ? (
            <input
              type="text"
              value={tempName}
              onChange={e => onTempNameChange(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onSave();
                }
              }}
            />
          ) : (
            <a
              href={`${BASE_URL}:${PORT}/g/${activeServer.id}/game/${game.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {game.game_name}
            </a>
          )}
        </div>
        <div className="game-actions">
          <a
            className="icon-button"
            href={`${BASE_URL}:${PORT}/g/${activeServer.id}/game/${game.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open Game"
          >
            <FontAwesomeIcon icon={faPlay} />
          </a>
          {isEditing ? (
            <a
              className="icon-button"
              onClick={onSave}
              title="Save Game Name"
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
            </a>
          ) : (
            <a
              className="icon-button"
              onClick={() => onTempNameChange(game.game_name, true)}
              title="Edit Game Name"
            >
              <FontAwesomeIcon icon={faPen} />
            </a>
          )}
          <a
            className="icon-button"
            onClick={onDelete}
            title="Delete Game"
          >
            <FontAwesomeIcon icon={faTrash} />
          </a>
          <label className="active-toggle">
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={game.active === "true"}
                onChange={onToggleActive}
              />
              <span className="slider" title="Enable/Disable Game"></span>
            </div>
          </label>
        </div>
      </div>
    );
  }, (prev, next) => {
    if (prev.isEditing && next.isEditing && prev.tempName === next.tempName) {
      return true;
    }
    return prev.game.id === next.game.id &&
      prev.game.game_name === next.game.game_name &&
      prev.game.active === next.game.active &&
      prev.isEditing === next.isEditing;
  });

  const handleChannelUpdated = (updatedChannel) => {
    setGuildChannelData(prev =>
      Array.isArray(prev)
        ? prev.map(channel =>
            channel.id === updatedChannel.id ? updatedChannel : channel
          )
        : [updatedChannel]
    );
  };

  const handleGameUpdated = (updatedGame) => {
    setGuildGameData(prev => prev.map(game => game.id === updatedGame.id ? updatedGame : game));
  };

  const handleChannelDeleted = (platform, deletedChannelId) => {
    axios
      .delete(`${API_BASE_URL}:${PORT}/api/channels/${activeServer.id}/${platform}/${deletedChannelId}`)
      .then(() => {
        console.log('Channel deleted:', deletedChannelId);
        setGuildChannelData(prev =>
          Array.isArray(prev)
            ? prev.filter(channel => channel.id !== deletedChannelId)
            : []
        );
      })
      .catch(err => console.error('Error deleting channel:', err));
  };

  const handleGameDeleted = (deletedGameId) => {
    axios.delete(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${deletedGameId}`)
      .then(() => {
        console.log('Game deleted:', deletedGameId)
        setGuildGameData(prev => prev.filter(game => game.id !== deletedGameId));
      })
      .catch(err => console.error('Error deleting game:', err));
  };

  const handleCreateChannel = () => {
    setShowNewChannelModal(true);
  };

  const handleCreateGame = () => {
    setShowNewGameModal(true);
  };

  const handleSubmitNewKickChannel = () => {
    window.location.href = `${API_BASE_URL}:${PORT}/api/kick/auth?guild_id=${activeServer.id}&user_id=${user.id}`;
  };

  // Handler for submitting the New Channel form.
  const handleSubmitNewChannel = (e) => {
    e.preventDefault();
    const newChannelData = {
      platform: newChannelForm.platform,
      channel: newChannelForm.channel,
      active: "true"
    };
  
    const tempId = "temp-" + Date.now();
    const optimisticChannel = { ...newChannelData, id: tempId };
  
    // Safely add the optimistic channel
    setGuildChannelData(prev => {
      console.log("Before optimistic update, prev:", prev);
      const current = Array.isArray(prev) ? prev : [];
      return [...current, optimisticChannel];
    });
  
    axios
      .post(
        `${API_BASE_URL}:${PORT}/api/channels/${activeServer.id}/${newChannelForm.platform}/${newChannelForm.channel}`,
        newChannelData
      )
      .then(({ data }) => {
        setGuildChannelData(prev => {
          console.log("Before replacing optimistic channel, prev:", prev);
          const current = Array.isArray(prev) ? prev : [];
          // Replace the optimistic channel (with tempId) with the data returned from the API
          return current.map(channel =>
            channel.id === tempId ? data : channel
          );
        });
        setShowNewChannelModal(false);
        setNewChannelForm({
          platform: "kick",
          channel: ""
        });
      })
      .catch(err => {
        console.error("Error creating new channel", err);
        setGuildChannelData(prev => {
          console.log("Before filtering out optimistic channel, prev:", prev);
          const current = Array.isArray(prev) ? prev : [];
          return current.filter(channel => channel.id !== tempId);
        });
      });
  };  

  // Handler for submitting the New Game form.
  const handleSubmitNewGame = (e) => {
    e.preventDefault();
    const newGameData = {
      game_name: newGameForm.game_name,
      game_type: newGameForm.game_type,
      keyword: newGameForm.keyword,
      keyword_enter: newGameForm.keyword_enter,
      keyword_style: newGameForm.keyword_style,
    };

    // Create an optimistic new game object with a temporary ID.
    const tempId = "temp-" + Date.now();
    const optimisticGame = { ...newGameData, id: tempId };

    // Add the optimistic game to state so it appears immediately.
    setGuildGameData(prev => [...prev, optimisticGame]);

    axios.post(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game`, newGameData)
      .then(({ data }) => {
        // Replace the optimistic game with the one returned by the API.
        setGuildGameData(prev =>
          prev.map(game => game.id === tempId ? data : game)
        );
        setShowNewGameModal(false);
        setNewGameForm({
          game_name: "",
          game_type: "raffle-basic",
          keyword_style: "=",
          keyword: "",
          keyword_enter: "false"
        });
      })
      .catch(err => {
        console.error("Error creating new game", err);
        // Optionally remove the optimistic game or notify the user.
        setGuildGameData(prev =>
          prev.filter(game => game.id !== tempId)
        );
      });
  };

  return (
    <div className="page-container">
      <div className="one-column">
        <h1>
          Welcome to {activeServer.name}, {user ? displayName : 'Guest'}
        </h1>
        <p>This page displays your guild settings along with the games and tools SquadBot provides.</p>
        <hr />
      </div>
      <div className="two-column">
        <div className="column">
          <h2>Channels</h2>
          <p>Manage your streaming platform(s) below.</p>
          <div className="guild-channels">
            {guildChannelData && Object.values(guildChannelData).length > 0 ? (
              Object.values(guildChannelData)
                .sort((a, b) => (a.id || "").localeCompare(b.id || ""))
                .map(channel => {
                  return (
                    <ChannelRow
                      key={channel.id}
                      platform={channel.platform}
                      channel={channel}
                      onToggleActive={() => {
                        const newActiveChannel = channel.active === "true" ? "false" : "true";
                        handleChannelUpdated({ ...channel, active: newActiveChannel });
                        axios
                          .put(`${API_BASE_URL}:${PORT}/api/channels/${activeServer.id}/${channel.platform}/${channel.id}`, { active: newActiveChannel })
                          .then(({ data }) => handleChannelUpdated(data))
                          .catch(err => console.error("Error toggling active", err));
                      }}
                      onDelete={() => handleChannelDeleted(channel.platform, channel.id)}
                    />
                  );
                })
            ) : (
              <p>No channels found. Add one below!</p>
            )}
            <div className="new-channel-row">
              <button className="new-channel-button icon-button" onClick={handleSubmitNewKickChannel}>
                <FontAwesomeIcon icon={faPlus} /> <img src="/img/kick/kick-streaming-logo-black.png" alt="Kick.com" />
              </button>
            </div>
          </div>
        </div>
        <div className="column">
          <h2>Games</h2>
          <p>Manage games/raffles/giveaways below.</p>
          <div className="guild-games">
            {guildGameData && guildGameData.length > 0 ? (
              [...guildGameData]
                .sort((a, b) => (a.id || "").localeCompare(b.id || ""))
                .map(game => {
                  // Your GameRow rendering here
                  // For example:
                  const isEditing = editingGames.hasOwnProperty(game.id);
                  const tempName = editingGames[game.id] || game.game_name;
                  return (
                    <GameRow
                      key={game.id}
                      game={game}
                      isEditing={isEditing}
                      tempName={tempName}
                      onTempNameChange={(newName, initiate = false) => {
                        setEditingGames(prev => ({ ...prev, [game.id]: newName }));
                      }}
                      onSave={() => {
                        axios
                          .put(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${game.id}`, { game_name: editingGames[game.id] })
                          .then(({ data }) => {
                            handleGameUpdated(data);
                            setEditingGames(prev => {
                              const updated = { ...prev };
                              delete updated[game.id];
                              return updated;
                            });
                          })
                          .catch(err => console.error("Error renaming game", err));
                      }}
                      onToggleActive={() => {
                        const newActive = game.active === "true" ? "false" : "true";
                        handleGameUpdated({ ...game, active: newActive }); // Optimistic update
                        axios
                          .put(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${game.id}`, { active: newActive })
                          .then(({ data }) => handleGameUpdated(data))
                          .catch(err => console.error("Error toggling active", err));
                      }}
                      onDelete={() => handleGameDeleted(game.id)}
                    />
                  );
                })
            ) : (
              <p>No games found. Add one below!</p>
            )}
            <div className="new-game-row">
              <button className="new-game-button" onClick={handleCreateGame}>
                <FontAwesomeIcon icon={faPlus} /> New Game
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="one-column">
        <hr />
      </div>
      <div className="one-column">
        <h2>Guild Settings</h2>
        <p>TODO: Display Discord Guild info and manage access/permissions.</p>
      </div>
      <div className="three-column">
        <div className="column">
          <h2>Guild Information</h2>
          <pre>{JSON.stringify(guildData, null, 2)}</pre>
        </div>
        <div className="column">
          <h2>Guild Admins</h2>
          <pre>{JSON.stringify(guildAdminData, null, 2)}</pre>
        </div>
        <div className="column">
          <h2>Guild Managers</h2>
          <pre>{JSON.stringify(guildManagerData, null, 2)}</pre>
        </div>
      </div>

      {/* New Channel Modal */}
      <Modal
        isOpen={showNewChannelModal}
        onRequestClose={() => setShowNewChannelModal(false)}
        overlayClassName="modal-overlay"
        className="modal-content"
      >
        <h2>Add New Channel</h2>
        <form onSubmit={handleSubmitNewChannel}>
          <div className="form-field">
            <label>Platform</label>
            <select
              value={newChannelForm.platform}
              onChange={(e) => setNewChannelForm({ ...newChannelForm, platform: e.target.value })}
            >
              <option value="kick">Kick.com</option>
            </select>
          </div>
          <div className="form-field">
            <label>Channel Name</label>
            <input
              type="text"
              value={newChannelForm.channel}
              onChange={(e) => setNewChannelForm({ ...newChannelForm, channel: e.target.value })}
              required
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={() => setShowNewChannelModal(false)}>Cancel</button>
            <button type="submit"><FontAwesomeIcon icon={faFloppyDisk} /> Save</button>
          </div>
        </form>
      </Modal>

      {/* New Game Modal */}
      <Modal
        isOpen={showNewGameModal}
        onRequestClose={() => setShowNewGameModal(false)}
        overlayClassName="modal-overlay"
        className="modal-content"
      >
        <h2>Create New Game</h2>
        <form onSubmit={handleSubmitNewGame}>
          <div className="form-field">
            <label>Game Name</label>
            <input
              type="text"
              value={newGameForm.game_name}
              onChange={(e) => setNewGameForm({ ...newGameForm, game_name: e.target.value })}
              required
            />
          </div>
          <div className="form-field">
            <label>Game Type</label>
            <select
              value={newGameForm.game_type}
              onChange={(e) => setNewGameForm({ ...newGameForm, game_type: e.target.value })}
            >
              <option value="raffle-basic">Basic Raffle</option>
              <option value="pie-wheel">Pie Wheel - WIP</option>
            </select>
          </div>
          <div className="form-field">
            <label>Keyword Style</label>
            <select
              value={newGameForm.keyword_style}
              onChange={(e) => setNewGameForm({ ...newGameForm, keyword_style: e.target.value })}
            >
              <option value="eq">=</option>
              {/* Add other options if needed */}
            </select>
          </div>
          <div className="form-field">
            <label>Keyword</label>
            <input
              type="text"
              value={newGameForm.keyword}
              onChange={(e) => setNewGameForm({ ...newGameForm, keyword: e.target.value })}
            />
          </div>
          <div className="form-field">
            <label>Keyword Enter</label>
            <input
              type="checkbox"
              checked={newGameForm.keyword_enter === "true"}
              onChange={(e) => setNewGameForm({ ...newGameForm, keyword_enter: e.target.checked ? "true" : "false" })}
            />
          </div>
          <div className="modal-actions">
            <button type="button" onClick={() => setShowNewGameModal(false)}>Cancel</button>
            <button type="submit"><FontAwesomeIcon icon={faFloppyDisk} /> Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GuildProfile;
